import { DateTime } from 'luxon';

import { TripsPageQuery } from '@website/types/graphqlOperations';

export type Group = TripsPageQuery['tripByURL']['groups']['items'][0];

/**
 * Add fully booked dummy groups between the regular ones, to create a bit of scarcity.
 */
export function addScarcityFullyBookedDates(
  groups: Group[],
  featureFlagEnabled: boolean,
): Group[] {
  // Just return the groups if the feature flag is not enabled.
  if (!featureFlagEnabled) {
    return groups;
  }

  // Just return if there are no groups
  if (groups.length === 0) {
    return groups;
  }

  const dayMillis = 24 * 60 * 60 * 1000;
  const weekMillis = 7 * dayMillis;

  // Check all the groups have the same duration. If not we can't add dummy groups so just return
  // the groups with no changes.
  const duration =
    (new Date(groups[0].end.utc).valueOf() -
      new Date(groups[0].start.utc).valueOf()) /
    dayMillis;
  if (
    !groups.every(
      (group) =>
        Math.round(
          (new Date(group.end.utc).valueOf() -
            new Date(group.start.utc).valueOf()) /
            dayMillis,
        ) === Math.round(duration),
    )
  ) {
    return groups;
  }

  const resultGroups: Group[] = [...groups];

  // For each group
  for (let i = 0; i < groups.length - 1; i++) {
    const group = groups[i];

    // Randomly continue with probability 0.5, using the last character of the group ID as a proxy
    // for a random seed so that the same groups will always be skipped or not.
    const addGroup: boolean = group.id.slice(-1).charCodeAt(0) % 2 === 0;
    if (!addGroup) {
       
      continue;
    }

    // Create a new start date a week later
    const newStartDate = new Date(
      new Date(group.start.utc).valueOf() + weekMillis,
    );

    // Check all existing groups to make sure there are no other groups that start then
    const dateAlreadyUsed = groups.some(
      (existingGroup) =>
        Math.round(new Date(existingGroup.start.utc).valueOf() / dayMillis) ===
        Math.round(newStartDate.valueOf() / dayMillis),
    );

    if (!dateAlreadyUsed) {
      const groupDuration = DateTime.fromISO(group.end.utc).diff(
        DateTime.fromISO(group.start.utc),
        'days',
      );

      // Note we keep the same ID so that it doesn't fully break the wait list (it means that
      // people will actually be joining the wait list of the real date rather than the dummy one).
      const dummyGroup: Group = {
        ...group,
        remainingSpaces: 0,
        start: {
          ...group.start,
          utc: newStartDate.toISOString(),
        },
        end: {
          ...group.end,
          utc: DateTime.fromISO(newStartDate.toISOString())
            .plus(groupDuration)
            .toISO(),
        },
      };

      resultGroups.push(dummyGroup);
    }
  }

  // Return sorted by start date (ascending)
  return resultGroups.sort(
    (a, b) => new Date(a.start.utc).valueOf() - new Date(b.start.utc).valueOf(),
  );
}
